import { Component, Input, SimpleChanges, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  BibData,
  Holding,
  HoldingLibrary,
  StaffFullRecordService,
} from './staff-full-record.service';
import { SharedService } from './../../../services/shared/shared.service';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { LayoutService } from '../../../services/layout.service';
import { StorageService } from '../../../services/storage/storage.service';

@Component({
  selector: 'staff-full-record',
  templateUrl: './staff-full-record.component.html',
  styleUrls: ['./staff-full-record.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StaffFullRecordComponent implements OnChanges {
  @Input() staff: any;

  loading = true;
  bibInfo: BibData;
  allHoldings: Holding[];
  newHolding: Holding;
  libraries: HoldingLibrary[];
  allLibraries: HoldingLibrary[];
  selectedLibrary: HoldingLibrary;
  selectLibraryHoldings: Holding[];
  selectedHolding: Holding;
  modifyReqId: number;
  generateNewLenderList: boolean;

  constructor(
    private _staffFullRecordService: StaffFullRecordService,
    private _layouyService: LayoutService,
    private _sharedService: SharedService,
    private _storeageService: StorageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.loading = true;
    if (changes.staff?.currentValue) {
      this.clearValues();
      if (this.staff.type === 'modifyRequest') {
        this.loading = false;
      } else if (this.staff.type === 'copyCatalog') {
        this._staffFullRecordService
          .newHolding(this.staff.libraryDBPoolKey, this.staff.agControlId)
          .subscribe((response) => {
            this.bibInfo = response.bibData;
            this.newHolding = response.newHolding;
            this.allLibraries = response.libraries;

            const previousSelectedLibCode = this._storeageService.getItem('selectedLibrary');
            if (previousSelectedLibCode) {
              this.selectedLibrary = this.allLibraries.find(
                (l) => l.libCode == previousSelectedLibCode,
              );
            }

            if (this.allLibraries.length == 1) {
              this.selectedLibrary = this.allLibraries[0];
            }

            this.loading = false;
          });
      } else {
        //addLocation, editLocation, deleteLocation
        this._staffFullRecordService
          .currentLocations(this.staff.agControlId, this.staff.libraryDBPoolKey)
          .subscribe((response) => {
            this.bibInfo = response.bibData;
            this.allHoldings = response.holdings;
            this.newHolding = response.newHolding;
            this.allLibraries = response.libraries;

            if (this.staff.type == 'addLocation') {
              if (this.allLibraries.length == 1) {
                this.selectedLibrary = this.allLibraries[0];
                this.onLibrarySelection();
              }
            } else {
              //A unique list of libraries from all the holdings
              this.libraries = this.uniqueLibraryList(response.holdings.map((h) => h.library));
              if (this.libraries.length == 1) {
                this.selectedLibrary = this.libraries[0];
                this.onLibrarySelection();
                if (this.selectLibraryHoldings.length == 1) {
                  this.selectedHolding = this.selectLibraryHoldings[0];
                }
              }
            }

            this.loading = false;
          });
      }
    }
  }

  onLibrarySelection() {
    if (this.staff.type === 'copyCatalog') {
      this._storeageService.setItem('selectedLibrary', this.selectedLibrary.libCode);
    }

    this.selectLibraryHoldings = this.allHoldings?.filter(
      (h) => h.library.libCode === this.selectedLibrary.libCode,
    );
    this.selectedHolding = null;
  }

  copyCatalog() {
    this.newHolding.hldDetails.find((h) => h.isLibraryField).value = this.selectedLibrary.libCode;
    this.newHolding.library = this.selectedLibrary;
    this.newHolding.callNumber = this.newHolding.hldDetails.find((h) => h.isCallNumber).value;
    this._staffFullRecordService
      .copyCatalog(this.bibInfo.libraryDBPoolKey, this.bibInfo.agControlId, this.newHolding)
      .subscribe(() => this.closeNav('Record has been added to main database'));
  }

  addLocation() {
    this.newHolding.hldDetails.find((h) => h.isLibraryField).value = this.selectedLibrary.libCode;
    this.newHolding.library = this.selectedLibrary;
    this.newHolding.callNumber = this.newHolding.hldDetails.find((h) => h.isCallNumber).value;
    this._staffFullRecordService
      .addLocation(this.bibInfo.libraryDBPoolKey, this.bibInfo.agControlId, this.newHolding)
      .subscribe(() => this.closeNav('Location Added'));
  }

  deleteLocation() {
    this._staffFullRecordService
      .deleteLocation(
        this.bibInfo.libraryDBPoolKey,
        this.bibInfo.agControlId,
        this.selectedHolding.holdingId,
      )
      .subscribe(() => this.closeNav('Location Deleted'));
  }

  updateLocation() {
    this._staffFullRecordService
      .updateLocation(this.bibInfo.libraryDBPoolKey, this.bibInfo.agControlId, this.selectedHolding)
      .subscribe(() => this.closeNav('Location Updated'));
  }

  submitModifyRequest() {
    const request = {
      agControlId: this.staff.agControlId,
      libraryDBPoolKey: this.staff.libraryDBPoolKey,
      requestId: +this.modifyReqId,
      generateNewLenderList: this.generateNewLenderList,
      sourceKey: this.staff.sourceKey,
    };

    this._staffFullRecordService.modifyExistingRequest(request).subscribe((response) => {
      this.closeNav(response.data);
    });
  }

  private clearValues(): void {
    this.bibInfo = null;
    this.allHoldings = null;
    this.newHolding = null;
    this.libraries = null;
    this.allLibraries = null;
    this.selectLibraryHoldings = null;
    this.selectedLibrary = null;
    this.selectedHolding = null;
    this.modifyReqId = null;
    this.generateNewLenderList = false;
    this.loading = true;
  }

  closeNav(message?: string): void {
    this.clearValues();
    if (message) {
      this._sharedService.openSnackBar(message);
    }
    this._layouyService.closeSideNav();
  }

  private uniqueLibraryList(libraries: HoldingLibrary[]): HoldingLibrary[] {
    const libCodes = Array.from(new Set(libraries.map((l) => l.libCode)));
    return libCodes.map((libCode) => libraries.find((l) => l.libCode == libCode));
  }
}
